<template>
    <div class="cp-store-locator__map" ref="google-map"></div>
</template>

<script>
import { createScript, loadScripts } from '../../helpers/loader';
import template from '../../helpers/template';
import debounce from '../../helpers/debounce';

const API = {
    callback: '__google_maps_api_provider__',
    channel: 'store-locator',
    client: null,
    key: 'AIzaSyCc_uU4fJnQfZ9Dl3P23vCer8tDmZb9FaM',
    language: null,
    // @see https://developers.google.com/maps/documentation/javascript/libraries
    libraries: [
        'geometry',
        'places',
    ],
    region: null,
    version: '3.18',
};

const DEPENDENCIES = {
    'markerclusterer': 'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/markerclusterer.js',
};

const STYLES = [
    {
        "elementType": "geometry",
        "stylers": [
            {
                "color": "#f5f5f5"
            }
        ]
    },
    {
        "elementType": "labels.icon",
        "stylers": [
            {
                "visibility": "off"
            }
        ]
    },
    {
        "elementType": "labels.text.fill",
        "stylers": [
            {
                "color": "#616161"
            }
        ]
    },
    {
        "elementType": "labels.text.stroke",
        "stylers": [
            {
                "color": "#f5f5f5"
            }
        ]
    },
    {
        "featureType": "administrative.country",
        "elementType": "geometry.stroke",
        "stylers": [
            {
                "color": "#909090"
            }
        ]
    },
    {
        "featureType": "administrative.land_parcel",
        "elementType": "labels.text.fill",
        "stylers": [
            {
                "color": "#bdbdbd"
            }
        ]
    },
    {
        "featureType": "poi",
        "elementType": "geometry",
        "stylers": [
            {
                "color": "#eeeeee"
            }
        ]
    },
    {
        "featureType": "poi",
        "elementType": "labels.text.fill",
        "stylers": [
            {
                "color": "#757575"
            }
        ]
    },
    {
        "featureType": "poi.park",
        "elementType": "geometry",
        "stylers": [
            {
                "color": "#e5e5e5"
            }
        ]
    },
    {
        "featureType": "poi.park",
        "elementType": "labels.text.fill",
        "stylers": [
            {
                "color": "#9e9e9e"
            }
        ]
    },
    {
        "featureType": "road",
        "elementType": "geometry",
        "stylers": [
            {
                "color": "#ffffff"
            }
        ]
    },
    {
        "featureType": "road.arterial",
        "elementType": "labels.text.fill",
        "stylers": [
            {
                "color": "#757575"
            }
        ]
    },
    {
        "featureType": "road.highway",
        "elementType": "geometry",
        "stylers": [
            {
                "color": "#dadada"
            }
        ]
    },
    {
        "featureType": "road.highway",
        "elementType": "labels.text.fill",
        "stylers": [
            {
                "color": "#616161"
            }
        ]
    },
    {
        "featureType": "road.local",
        "elementType": "labels.text.fill",
        "stylers": [
            {
                "color": "#9e9e9e"
            }
        ]
    },
    {
        "featureType": "transit.line",
        "elementType": "geometry",
        "stylers": [
            {
                "color": "#e5e5e5"
            }
        ]
    },
    {
        "featureType": "transit.station",
        "elementType": "geometry",
        "stylers": [
            {
                "color": "#eeeeee"
            }
        ]
    },
    {
        "featureType": "water",
        "elementType": "geometry",
        "stylers": [
            {
                "color": "#c9c9c9"
            }
        ]
    },
    {
        "featureType": "water",
        "elementType": "labels.text.fill",
        "stylers": [
            {
                "color": "#9e9e9e"
            }
        ]
    }
];

const MAP = {
    // Set to false to deactivate clusters
    // @see https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/markerclusterer.js
    clusters: false,
    /*clusters: {
        imagePath: 'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m',
        ignoreHidden: true,
        maxZoom: 10,
        styles: null,
    },*/

    geocode: false,

    // Set to false to deactivate location marker and circle
    location: false,
    /*location: {
        // Set to false to deactivate location circle
        circle: {
            fillColor: '#213B87',
            fillOpacity: 0.35,
            radius: 5000,
            strokeColor: '#213B87',
            strokeOpacity: 0.8,
            strokeWeight: 2,
        },
        // Set to false to deactivate location marker
        icon: 'https://www.google.com/mapfiles/marker_green.png',
    },*/

    // Default stores markers
    markers: {
        // String or Object
        icon: '/app/themes/cherrypulp/public/images/marker.svg',
    },

    // @see https://developers.google.com/maps/documentation/javascript/tutorial
    config: {
        center: {
            lat: 50.6740609,
            lng: 4.470022,
        },
        fullscreenControl: false,
        gestureHandling: 'cooperative',
        mapTypeControl: false,
        mapTypeControlOptions: false,
        scrollwheel: false,
        streetViewControl: false,
        styles: STYLES,
        zoom: 8,
    },
};

let loading = false;

export default {
    name: "cp-store-locator-map",
    props: {
        config: {
            type: Object,
            required: true,
        },

        stores: {
            type: Array,
            required: true,
        },
    },
    data() {
        return {
            circle: null,
            clusters: null,
            infoWindows: {},
            location: null,
            map: null,
            markers: [],
        };
    },
    watch: {
        /**
         * On stores updates: reset markers, clusters and unactive selected marker.
         * @param stores
         */
        stores(stores) {
            if (window.google === undefined) {
                return;
            }

            this.createMarkers(stores);
        },
    },
    methods: {
        /**
         * Center map to the given lat, lng and set the zoom level.
         * @param lat
         * @param lng
         * @param zoom
         */
        centerMap(lat, lng, zoom = null) {
            if ( !this.map ) {
                return;
            }

            zoom = zoom || this.zoom;

            this.map.setZoom(this.zoom);
            this.map.setCenter(new window.google.maps.LatLng(lat, lng));
            this.map.setZoom(zoom);
        },

        /**
         * If exists, remove the radius circle from the map.
         */
        clearCircle() {
            if (this.circle) {
                this.circle.setMap(null);
                this.circle = null;
            }
        },

        /**
         * If exists, remove all clusters from the map.
         */
        clearClusters() {
            if (this.clusters) {
                this.clusters.clearMarkers();
            }
        },

        /**
         * If exists, remove the current location marker from the map.
         */
        clearCurrentMarker() {
            if (this.location) {
                this.location.setMap(null);
                this.location = null;
            }
        },

        /**
         * Remove all markers from the map.
         * @param remove
         */
        clearMarkers(remove = false) {
            this.markers.map((marker) => marker.setMap(null));

            if (remove) {
                this.markers = [];
            }
        },

        /**
         * Create a circle radius on the given lat, lng with the given radius.
         * @param lat
         * @param lng
         * @param radius
         * @param options
         */
        createCircle(lat, lng, radius = 5000, options = {}) {
            options = Object.assign({
                center: new window.google.maps.LatLng(lat, lng),
                fillColor: '#f5a68c',
                fillOpacity: 0.15,
                map: this.map,
                radius,
                strokeColor: '#f5a68c',
                strokeOpacity: 0.4,
                strokeWeight: 2,
            }, options);

            if (this.circle) {
                this.circle.setMap(null);
            }

            this.circle = new window.google.maps.Circle(options);
            // window.google.maps.event.addListener(this.circle, 'click', () => this.$events.emit('disable-marker'));
        },

        /**
         * Create clusters on the map.
         */
        createClusters() {
            if ( this.clusters ) {
                this.clearClusters();
            }

            // @see https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/markerclusterer.js
            this.clusters = new window.MarkerClusterer( this.map, this.markers, MAP.clusters );
        },

        /**
         * Create a current location marker on given lat, lng.
         * @param lat
         * @param lng
         */
        createCurrentMarker(lat, lng) {
            if ( this.location ) {
                this.location.setMap( null );
            }

            this.location = this.createMarker( lat, lng );
        },

        /**
         * Instantiate the map.
         */
        createMap() {
            if ( this.map ) {
                return;
            }

            const config = Object.assign({
                infoWindow: new window.google.maps.InfoWindow( { maxWidth: 500 } ),
            }, MAP.config, this.config );

            if ( window.google ) {
                this.map = new window.google.maps.Map( this.$refs['google-map'], config );

                this.map.addListener('click', () => this.$events.emit('disable-marker'));
                this.map.addListener('bounds_changed', debounce( () => this.displayMarkers(), 25 ) );
            } else {
                throw new Error('[store-locator] Google Map API is not loaded!');
            }
        },

        /**
         * Create a marker on the given lat, lng.
         * @param lat
         * @param lng
         * @param options
         * @return {window.google.maps.Marker}
         */
        createMarker(lat, lng, options = {}) {
            options = {
                icon: MAP.markers.icon,
                map: this.map,
                position: new window.google.maps.LatLng(lat, lng),
                ...options,
            };

            return new window.google.maps.Marker( options );
        },

        /**
         * Create all markers from the given stores.
         * @param stores
         */
        createMarkers(stores) {
            if ( !Array.isArray( stores ) ) {
                stores = [ stores ];
            }

            if ( this.markers.length ) {
                this.clearMarkers(true);
            }

            this.markers = stores.map( store => {
                const {lat, lng, name} = store;
                const uid = this.$actions.uid( { lat, lng, name } );
                const icon = store.marker ? store.marker : MAP.markers.icon;

                let markerOptions = {
                    icon: typeof icon === 'string' ? icon : icon.inactive,
                    id: uid,
                    title: store.title,
                };

                // @note - look https://github.com/scottdejonge/map-icons
                const marker = this.createMarker( lat, lng, markerOptions );

                // this.$events.on('disable-marker', () => marker.setIcon( typeof icon === 'string' ? icon : icon.inactive ) );
                window.google.maps.event.addListener( marker, 'click', () => {
                    this.$events.emit('center-map', lat, lng, 15 );
                    this.$events.emit('active-marker', marker.id );
                } );

                window.google.maps.event.addListener( marker, 'mouseover', () => this.$events.emit('create-circle', lat, lng ) );
                window.google.maps.event.addListener( marker, 'mouseout', () => this.$events.emit('clear-circle') );

                return marker;
            });
        },

        /**
         * Show only markers in visible part of the map (= bounds).
         */
        displayMarkers() {
            //this.$actions.filter( ( store ) => this.map.getBounds().contains( store ));
            this.$actions.applyFilters();
        },

        /**
         * Geolocate user on load.
         */
        geolocateCenter() {
            if ( MAP.geocode && navigator.geolocation ) {
                navigator.geolocation.getCurrentPosition(position => {
                    this.centerMap( position.coords.latitude, position.coords.longitude, 12 );
                    this.createCircle( position.coords.latitude, position.coords.longitude );
                    this.createMarker( position.coords.latitude, position.coords.longitude );
                }, console.error);
            }
        },

        /**
         * Load Google Maps Api and dependencies
         * @param options
         */
        loadGoogleMapsApi(options) {
            this.$actions.loading( true );

            new Promise(( resolve, reject ) => {
                if (window.google) {
                    resolve(window.google);
                } else if (loading) {
                    let interval = setInterval( () => {
                        if (window.google) {
                            interval && clearInterval( interval );

                            loading = false;
                            resolve(window.google);
                        }
                    }, 10);
                } else {
                    loading = false;

                    options = Object.assign({}, DEFAULTS, options);

                    window[options.callback] = () => {
                        loading = false;
                        resolve(window.google);
                    };

                    const params = Object.keys( options )
                        .map( key => encodeURIComponent( key ) + '=' + encodeURIComponent( options[ key ] ) )
                        .join('&');

                    const url = 'https://maps.googleapis.com/maps/api/js?' + params;

                    createScript(url, null, reject);
                }
            })
                .then( google => {
                    loadScripts( DEPENDENCIES, () => {
                        this.$events.emit('api-loaded', google);
                        this.$actions.loading(false);
                    });
                });
        },
    },
    mounted() {
        // this.$events.on('disable-marker', () => this.map.infoWindow.close());
        this.$events.on('center-map', (lat, lng, zoom) => this.centerMap(lat, lng, zoom));
        this.$events.on('clear-circle', () => this.clearCircle());
        this.$events.on('clear-current-marker', () => this.clearCurrentMarker());
        this.$events.on('create-circle', (lat, lng, radius, options) => this.createCircle(lat, lng, radius, options));
        this.$events.on('create-current-marker', (lat, lng) => this.createCurrentMarker(lat, lng));
        this.$events.on('display-markers', () => this.displayMarkers());

        this.$events.on('api-loaded', () => {
            this.createMap();
            // this.createMarkers(this.stores);

            if ( MAP.clusters ) {
                this.createClusters();
            }

            if ( MAP.geocode ) {
                this.geolocateCenter();
            }

            this.$actions.registerFilter( stores => {
                const mapBounds = this.map.getBounds();

                // keep stores distanced to the searched location (radius allow to enlarge search perimeter)

                return stores.filter( store => {
                    if ( !store.radius || !this.location ) {
                        return mapBounds.contains( store );
                    }

                    const storeLatLng = new window.google.maps.LatLng( store.lat, store.lng );

                    return (
                        mapBounds.contains( storeLatLng ) ||
                        window.google.maps.geometry.spherical.computeDistanceBetween( this.location.position, storeLatLng ) < store.radius
                    );
                } );
            } );
        }) ;

        this.loadGoogleMapsApi( API );
    },
}
</script>
